import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addState,
  flowMax,
} from 'ad-hok'
import {
  addDebouncedCopy,
  addEffectOnMount,
  addPropTrackingRef,
} from 'ad-hok-utils'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi, addUser } from 'shared/src/api'
import { Edit } from 'shared/src/api/types'
import { addIntPathParam, addNavigate } from 'shared/src/utils/addNavigate'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'
import { formatDate } from 'shared/src/utils/formatting'
import { typedAs } from 'shared/src/utils/typedAs'

import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { Modal } from 'components/Modal'
import { NextActionButton } from 'components/NextActionButton'
import {
  BODY_MEDIUM_STYLE,
  MAIN_TITLE_STYLE,
  NOTES_BOLD_STYLE,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
} from 'components/styles'
import { addToastContext } from 'components/toastContext'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeEditUserListRoute } from 'utils/routes'

export const MAX_EDIT_NAME_LENGTH = 50
const SIDE_PADDING = 24

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    background: '#F9F6F4',
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    paddingTop: 26,
    height: '100dvh',
  },
  header: {
    display: 'flex',
    gap: 8,
    marginBottom: 8,
    alignItems: 'center',
  },
  title: {
    ...MAIN_TITLE_STYLE,
    color: '#232323',
  },
  subtitle: {
    ...NOTES_BOLD_STYLE,
    color: '#90734F',
    marginLeft: 32,
  },
  close: {
    position: 'relative',
    right: -10,
    alignSelf: 'center',
  },
  closeIcon: {
    color: '#90734F',
  },
  createNewButton: {
    marginBottom: 32,
    flexShrink: 0,
  },
  inputContainer: {
    width: '100%',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    top: 0,
    right: 16,
    height: '100%',
  },
  input: {
    ...BODY_MEDIUM_STYLE,
    width: '100%',
    height: 62,
    caretColor: '#90734F',
    outline: 'none',
    color: '#90734F',
    marginBottom: 32,
    marginTop: 32,
    paddingRight: 40,
    '&::placeholder': {
      color: 'rgba(144, 115, 79, 0.56)',
      opacity: 1,
    },
    borderRadius: 4,
    border: '1px solid rgba(144, 115, 79, 0.24)',
    background: 'rgba(54, 52, 50, 0.02)',
    '&:focus': {
      border: '1px solid rgba(144, 115, 79, 0.56)',
    },
  },
  rowsContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    gap: 8,
    paddingBottom: 32,
  },
  row: {
    height: 64,
    paddingTop: 25,
    paddingBottom: 25,
    paddingLeft: 16,
    paddingRight: 24,
    backgroundColor: 'rgba(203, 166, 122, 0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  rowText: {
    color: 'rgba(144, 115, 79, 0.80)',
    fontFamily: SERIF_FONT_FAMILY,
    fontSize: 24,
    fontStyle: 'italic',
    fontWeight: 700,
    lineHeight: '140%',
    letterSpacing: '0.4px',
    maxWidth: 270,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dateText: {
    color: 'rgba(54, 52, 50, 0.56)',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.2,
    fontFamily: SANS_SERIF_FONT_FAMILY,
  },
  rightArrow: {
    height: 16,
    width: 16,
    color: '#CBA67A',
  },
})

interface CreateNewEditButtonProps {
  name: string
  onClick: () => void
}

const CreateNewEditButton: FC<CreateNewEditButtonProps> = flowMax(
  addDisplayName('CreateNewEditButton'),
  addTranslationHelpers,
  addApi,
  addToastContext,
  addUser,
  addNavigate,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ name, onClick, classes, t }) => (
    <NextActionButton
      icon="edit-new"
      onClick={() => onClick()}
      text={`${t('admin.userEdits.createNew')} "${name}"`}
      className={classes.createNewButton}
    />
  )
)

interface RowProps {
  edit: Edit
  onClick: () => void
}

const Row: FC<RowProps> = flowMax(
  addDisplayName('Row'),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ edit: { name, createdAt }, onClick, classes }) => (
    <button onClick={onClick} className={classes.row}>
      <div className={classes.rowTextContainer}>
        <span className={classes.rowText}>{name}</span>
        <span className={classes.dateText}>{formatDate(createdAt)}</span>
      </div>
      <Icon name="chevron-right" className={classes.rightArrow} />
    </button>
  )
)

interface Props {
  onCancel: () => void
}

export const EditSearch: FC<Props> = flowMax(
  addDisplayName('EditSearch'),
  addApi,
  addIntPathParam('userId'),
  addUser,
  addDataLoadingFromPromise(
    'edits',
    ({ user, userId, api }) => api.edits.search('', userId ?? user.id),
    [],
    () => 'edit-search'
  ),
  addProps(({ edits }) => ({
    edits: hasData(edits) ? edits.data : [],
  })),
  addState('searchResults', 'setSearchResults', typedAs<Edit[]>([])),
  addState('searchText', 'onChangeSearchText', ''),
  addDebouncedCopy(200, 'searchText', 'searchTextDebounced'),
  addPropTrackingRef('searchText', 'searchTextRef'),
  addEffect(
    ({
        user,
        userId,
        searchTextDebounced,
        api,
        searchTextRef,
        setSearchResults,
      }) =>
      () => {
        if (searchTextRef.current !== searchTextDebounced) return

        api.edits.search!(searchTextDebounced, userId ?? user.id).then(
          (results) => {
            setSearchResults(results)
          }
        )
      },
    ['searchTextDebounced']
  ),
  addHandlers({
    onChange:
      ({ onChangeSearchText }) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChangeSearchText(
          event.target.value.substring(0, MAX_EDIT_NAME_LENGTH)
        )
      },
  }),
  addRef('inputRef', null as HTMLInputElement | null),
  addEffectOnMount(({ inputRef }) => () => {
    inputRef.current!.focus()
  }),
  addToastContext,
  addNavigate,
  addState('isSaving', 'setIsSaving', false),
  addTranslationHelpers,
  addHandlers({
    onSelect:
      ({
        searchText,
        user,
        userId,
        api,
        showErrorToast,
        setIsSaving,
        navigate,
        t,
      }) =>
      async (edit?: Edit) => {
        try {
          const userList = await api.userLists.create({
            userId: userId ?? user.id,
            name: !!edit ? edit.name : searchText,
            editId: edit?.id,
            isPublic: true,
            productIds: [],
          })

          navigate(makeEditUserListRoute(userList.id, userId))
        } catch (e) {
          console.log(e)
          showErrorToast({
            title: t('admin.userEdits.errorCreatingUserList'),
            details: t('admin.general.genericError'),
          })
        } finally {
          setIsSaving(false)
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    inputRef,
    searchText,
    searchResults,
    onChange,
    onSelect,
    onCancel,
    classes,
    t,
  }) => (
    <Modal containerClassName={classes.container}>
      <div className={classes.header}>
        <IconButton name="back" onClick={onCancel} />
        <span className={classes.title}>
          {t('admin.userEdits.search.title')}
        </span>
      </div>
      <span className={classes.subtitle}>
        {t('admin.userEdits.search.subtitle')}
      </span>
      <div className={classes.inputContainer}>
        <input
          placeholder={t('admin.userEdits.search.placeholder')}
          onChange={onChange}
          ref={inputRef}
          className={cx(classes.input, 'focus:ring-0')}
        />
        <Icon name="search" className={classes.searchIcon} />
      </div>
      <div className={classes.rowsContainer}>
        {searchResults.map((edit) => (
          <Row
            edit={edit}
            onClick={() => {
              onSelect(edit)
            }}
            key={edit.id}
          />
        ))}
        {searchText.length > 0 && (
          <CreateNewEditButton
            name={searchText}
            onClick={onSelect}
            key={searchText}
          />
        )}
      </div>
    </Modal>
  )
)
